// Save export / import (owner-approved 2026-11-02): back up the profile or move it between devices without accounts.
// Pure logic with injected storage (unit-tested in tests/saveTransfer.test.ts); the dialog is src/app/saveMenu.ts.
//
// A bundle = { format, fv, v, at, profile, prefs, sum }:
//   profile  the full versioned profile (Workshop, shards, records, ledger, Encyclopedia, accessibility settings)
//   prefs    every other 'arena.*' setting in localStorage (button mapping, volumes, camera, glyphs, …) except the
//            per-device performance ones (quality, frame cap, dynamic resolution, render scale, particle / grass density)
//            and the save slots themselves
//   sum      checksum over the canonical JSON of { fv, v, profile, prefs } — hand-edited or truncated files are refused
// As a file it is pretty JSON (.json); as a code (phones / TV / paste) it is 'SLA1.' + base64url(JSON).
// Import validates format → version → checksum → schema, migrates older versions through save.ts migrate(), and keeps
// the replaced save (with its prefs) in PREV_KEY so "Restore previous save" can undo it (restoring swaps, so it is
// undoable too).
import { CODEX_TABS, counts } from './codex';
import { BACKUP_KEY, KV, Profile, SAVE_KEY, SAVE_VERSION, migrate } from './save';

export const EXPORT_FORMAT = 'shatterlight-save';
/** bundle wrapper version (independent of the profile's SAVE_VERSION) */
export const EXPORT_FV = 1;
export const CODE_PREFIX = 'SLA1.';
/** the save an import / restore replaced (a full bundle, so prefs come back too) */
export const PREV_KEY = 'arena.save.prev';
const PREF_PREFIX = 'arena.';
/** per-device performance settings never travel between devices */
/** (arena.backend: the retired Renderer option; a stale value is ignored at boot and never exported)
 *  (renderscale / particles / grass: the per-device graphics overrides of Settings › Advanced, 2026-11-02) */
export const DEVICE_PREFS: readonly string[] = ['arena.quality', 'arena.backend', 'arena.fpscap', 'arena.dynres', 'arena.renderscale', 'arena.particles', 'arena.grass'];
const SLOT_KEYS: readonly string[] = [SAVE_KEY, BACKUP_KEY, PREV_KEY];
/** pre-v4 accessibility keys: the profile's a11y block replaced them (they only seed an old save once) */
const LEGACY_KEYS: readonly string[] = ['arena.flashes', 'arena.shake', 'arena.uisize'];
const MAX_PREFS = 96, MAX_PREF_LEN = 32768, MAX_TEXT = 2_000_000;

/** storage that can also list and remove keys (localStorage) */
export interface ListKV extends KV { removeItem(k: string): void; keys(): string[] }

export interface SaveBundle { format: string; fv: number; v: number; at: string; profile: unknown; prefs: Record<string, string>; sum: string }
export interface SaveSummary {
  version: number; shards: number; invested: number; earned: number; workshopRanks: number;
  runs: number; wins: number; bestTime: number; bestKills: number; bestLevel: number; /** SURGE FORMS: best form reached (0 = none) */ bestForm: number;
  discovered: number; discoverable: number; prefs: number; exportedAt: string | null;
}
export type ImportError = 'empty' | 'unreadable' | 'not-a-save' | 'newer-version' | 'checksum' | 'bad-data';
export type ImportResult =
  | { ok: true; profile: Profile; prefs: Record<string, string>; fromVersion: number; migrated: boolean; summary: SaveSummary }
  | { ok: false; error: ImportError; message: string };

export const ERROR_TEXT: Record<ImportError, string> = {
  empty: 'Nothing to import: choose a file or paste a save code first.',
  unreadable: 'That is not a readable save file or code (it may be cut short).',
  'not-a-save': 'That file is not a Shatterlight Arena save.',
  'newer-version': 'That save comes from a newer version of the game. Refresh to update, then import again.',
  checksum: 'The save failed its integrity check: it was edited or damaged, so it was not imported.',
  'bad-data': 'The save is missing required data, so it was not imported.',
};

// ---------------------------------------------------------------------------------------------------------------------
/** JSON with object keys sorted at every level (stable across parse / stringify round trips) */
export function canonical(x: unknown): string {
  if (x === null || typeof x !== 'object') return JSON.stringify(x) ?? 'null';
  if (Array.isArray(x)) return '[' + x.map(canonical).join(',') + ']';
  const o = x as Record<string, unknown>;
  return '{' + Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => JSON.stringify(k) + ':' + canonical(o[k])).join(',') + '}';
}

/** two FNV-1a 32-bit lanes over the UTF-16 code units → 16 hex chars (integrity, not security) */
export function checksum(s: string): string {
  let a = 0x811c9dc5, b = 0x01000193 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ ((c * 31 + i) & 0xffff), 0x01000193) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}
const sumOf = (fv: number, v: number, profile: unknown, prefs: Record<string, string>) => checksum(canonical({ fv, v, profile, prefs }));

// ---------------------------------------------------------------------------------------------------------------------
/** the settings that travel with a save: every 'arena.*' key except the save slots and per-device ones */
export function collectPrefs(kv: ListKV): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of kv.keys().sort()) {
    if (!isPortablePref(k)) continue;
    const v = kv.getItem(k); if (v !== null && v.length <= MAX_PREF_LEN) out[k] = v;
  }
  return out;
}
export function isPortablePref(k: string): boolean {
  return k.startsWith(PREF_PREFIX) && !SLOT_KEYS.includes(k) && !DEVICE_PREFS.includes(k) && !LEGACY_KEYS.includes(k) && k.length <= 64;
}

export function makeBundle(profile: Profile, prefs: Record<string, string>, now: Date = new Date()): SaveBundle {
  const p = JSON.parse(JSON.stringify(profile)) as Profile; // detached copy
  return { format: EXPORT_FORMAT, fv: EXPORT_FV, v: p.v, at: now.toISOString(), profile: p, prefs: { ...prefs }, sum: sumOf(EXPORT_FV, p.v, p, prefs) };
}
export const bundleToJson = (b: SaveBundle): string => JSON.stringify(b, null, 1);

const b64 = {
  enc(s: string): string {
    const bytes = new TextEncoder().encode(s); let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  },
  dec(s: string): string {
    const t = s.replace(/-/g, '+').replace(/_/g, '/'), bin = atob(t + '='.repeat((4 - (t.length % 4)) % 4));
    const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  },
};
export const bundleToCode = (b: SaveBundle): string => CODE_PREFIX + b64.enc(JSON.stringify(b));
/** a filename like shatterlight-save-2026-11-02.json */
export const exportFileName = (now: Date = new Date()): string => `shatterlight-save-${now.toISOString().slice(0, 10)}.json`;

// ---------------------------------------------------------------------------------------------------------------------
export function summarize(p: Profile, prefs: Record<string, string> = {}, exportedAt: string | null = null): SaveSummary {
  let discovered = 0, discoverable = 0;
  for (const tab of CODEX_TABS) { const c = counts(tab, p.codex); discovered += c.found; discoverable += c.total; }
  return {
    version: p.v, shards: p.shards, invested: p.invested, earned: p.earned,
    workshopRanks: Object.values(p.workshop).reduce((a, b) => a + (b ?? 0), 0),
    runs: p.records.runs, wins: p.records.wins, bestTime: p.records.bestTime, bestKills: p.records.bestKills, bestLevel: p.records.bestLevel, bestForm: p.records.bestForm ?? 0,
    discovered, discoverable, prefs: Object.keys(prefs).length, exportedAt,
  };
}

const fail = (error: ImportError): ImportResult => ({ ok: false, error, message: ERROR_TEXT[error] });
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const okNum = (x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= 0;

/** Parse a save file's text or a pasted code. Never throws. */
export function parseImport(text: string): ImportResult {
  const t = (text ?? '').trim();
  if (!t) return fail('empty');
  if (t.length > MAX_TEXT) return fail('unreadable');
  let json = t;
  if (!t.startsWith('{')) {
    const code = t.replace(/\s+/g, ''); // pasted codes may be wrapped across lines
    if (!code.startsWith(CODE_PREFIX)) return fail(/^[A-Za-z0-9_-]{16,}$/.test(code) ? 'unreadable' : 'not-a-save');
    try { json = b64.dec(code.slice(CODE_PREFIX.length)); } catch { return fail('unreadable'); }
  }
  let raw: unknown;
  try { raw = JSON.parse(json); } catch { return fail('unreadable'); }
  if (!isObj(raw) || raw.format !== EXPORT_FORMAT) return fail('not-a-save');
  const fv = raw.fv, v = raw.v;
  if (typeof fv !== 'number' || typeof v !== 'number' || !Number.isInteger(v) || v < 1) return fail('bad-data');
  if (fv > EXPORT_FV || v > SAVE_VERSION) return fail('newer-version');
  const prefsRaw = raw.prefs === undefined ? {} : raw.prefs;
  if (!isObj(prefsRaw) || typeof raw.sum !== 'string') return fail('bad-data');
  if (sumOf(fv, v, raw.profile, prefsRaw as Record<string, string>) !== raw.sum) return fail('checksum');
  // schema: the minimum every version has had (shards wallet + records); everything else migrates
  const pr = raw.profile;
  if (!isObj(pr) || !okNum(pr.shards) || !isObj(pr.records) || (pr.v !== undefined && pr.v !== v)) return fail('bad-data');
  if (pr.workshop !== undefined && !isObj(pr.workshop)) return fail('bad-data');
  const prefs: Record<string, string> = {};
  for (const [k, val] of Object.entries(prefsRaw)) {
    if (Object.keys(prefs).length >= MAX_PREFS) break;
    if (isPortablePref(k) && typeof val === 'string' && val.length <= MAX_PREF_LEN) prefs[k] = val;
  }
  const profile = migrate(pr);
  return { ok: true, profile, prefs, fromVersion: v, migrated: v !== SAVE_VERSION, summary: summarize(profile, prefs, typeof raw.at === 'string' ? raw.at : null) };
}

// ---------------------------------------------------------------------------------------------------------------------
function writePrefs(kv: ListKV, prefs: Record<string, string>): void {
  for (const k of kv.keys()) if (isPortablePref(k) && !(k in prefs)) kv.removeItem(k); // settings the import doesn't have go back to defaults
  for (const [k, v] of Object.entries(prefs)) if (isPortablePref(k)) kv.setItem(k, v);
}

/** Replace the current save (+ its portable settings) with an imported one, keeping the current one in PREV_KEY. */
export function applyImport(kv: ListKV, current: Profile, incoming: { profile: Profile; prefs: Record<string, string> }, now: Date = new Date()): void {
  kv.setItem(PREV_KEY, JSON.stringify(makeBundle(current, collectPrefs(kv), now)));
  kv.setItem(SAVE_KEY, JSON.stringify(incoming.profile));
  writePrefs(kv, incoming.prefs);
}

/** The save an import / restore replaced, if any (validated like an import). */
export function readPrevious(kv: KV): ImportResult | null {
  const s = kv.getItem(PREV_KEY);
  return s ? parseImport(s) : null;
}

/** Swap the current save with the previous one (so a restore can itself be undone). false when there is none / it is damaged. */
export function restorePrevious(kv: ListKV, current: Profile, now: Date = new Date()): boolean {
  const prev = readPrevious(kv);
  if (!prev || !prev.ok) return false;
  applyImport(kv, current, prev, now);
  return true;
}

/** localStorage as a ListKV (null when storage is blocked) */
export function browserListKV(): ListKV | null {
  try {
    const ls = localStorage; const k = '__t'; ls.setItem(k, '1'); ls.removeItem(k);
    return { getItem: (x) => ls.getItem(x), setItem: (x, v) => ls.setItem(x, v), removeItem: (x) => ls.removeItem(x), keys: () => Array.from({ length: ls.length }, (_, i) => ls.key(i)!).filter(Boolean) };
  } catch { return null; }
}
